/* ── DreamLog — Adaptive Sleep Tracker 0–24m ────────────────────────────── */
'use strict';

// ── Storage keys ─────────────────────────────────────────────────────────────
const SL_KEY       = 'sl_logs';
const ROUTINE_KEY  = 'sl_routines';
const GOALS_KEY    = 'sl_goals';
const PREFS_KEY    = 'sl_prefs';
const ACTIVE_KEY   = 'sl_active';
const REGRESS_KEY  = 'sl_regression';

// ── Wake window table (age in weeks → { minMin, maxMin }) ────────────────────
const WAKE_WINDOWS = [
  { maxWeeks: 6,  min: 45,  max: 60,  label: '45–60 min' },
  { maxWeeks: 12, min: 60,  max: 90,  label: '60–90 min' },
  { maxWeeks: 16, min: 90,  max: 120, label: '1.5–2 h' },
  { maxWeeks: 24, min: 105, max: 150, label: '1.75–2.5 h' },
  { maxWeeks: 32, min: 120, max: 180, label: '2–3 h' },
  { maxWeeks: 40, min: 150, max: 210, label: '2.5–3.5 h' },
  { maxWeeks: 52, min: 180, max: 240, label: '3–4 h' },
  { maxWeeks: 65, min: 210, max: 300, label: '3.5–5 h' },
  { maxWeeks: 78, min: 240, max: 360, label: '4–6 h' },
  { maxWeeks: 999,min: 300, max: 360, label: '5–6 h' },
];

// ── Stage definitions ─────────────────────────────────────────────────────────
const STAGES = [
  { id: 1, label: 'Newborn',       minMonths: 0,  maxMonths: 3  },
  { id: 2, label: 'Early Infant',  minMonths: 3,  maxMonths: 6  },
  { id: 3, label: 'Late Infant',   minMonths: 6,  maxMonths: 12 },
  { id: 4, label: 'Early Toddler', minMonths: 12, maxMonths: 18 },
  { id: 5, label: 'Late Toddler',  minMonths: 18, maxMonths: 25 },
];

// ── Regression windows ────────────────────────────────────────────────────────
const REGRESSIONS = [
  {
    key: '4_month',
    label: '4-Month Regression',
    minWeeks: 14, maxWeeks: 20,
    guide: 'Night wakings usually peak within 1–2 weeks. This is a sign of healthy brain maturation — sleep cycles are becoming more adult-like.',
    signal: 'Increased night wakings over 3+ consecutive nights'
  },
  {
    key: '8_month',
    label: '8-Month Regression',
    minWeeks: 32, maxWeeks: 40,
    guide: 'The 8-month regression often brings nap refusals and more night wakings. It coincides with a major developmental leap — sitting, crawling, object permanence.',
    signal: 'Nap refusals and increased night wakings over 4+ days'
  },
  {
    key: '12_month',
    label: '12-Month Regression',
    minWeeks: 48, maxWeeks: 56,
    guide: 'The 12-month regression is driven by walking, language development, and separation anxiety. It usually passes within 2–4 weeks.',
    signal: 'Bedtime resistance and increased night wakings'
  },
  {
    key: '18_month',
    label: '18-Month Regression',
    minWeeks: 74, maxWeeks: 90,
    guide: 'The 18-month regression is often the most intense. Your toddler is verbal, mobile, and testing limits. Consistency in the bedtime routine is the most reliable tool.',
    signal: 'Settle time over 30 min and increased wakings for 5+ days'
  },
];

// ── State ─────────────────────────────────────────────────────────────────────
let logs       = [];
let routines   = [];
let goals      = {};
let prefs      = { babyName: '', birthdate: '', gestationalAge: null, onboardingDone: false, regressionCardsSeen: [], napMode: null };
let activeSleep = null;
let screen     = 'home';
let slTimerHandle   = null;  // home screen tick
let routineTimer    = null;  // routine tick
let routineStartTs  = null;
let settleVal       = 0;
let wakingsVal      = 0;
let wkVal           = 0;     // quick wakings sheet

// ── DOM helpers ───────────────────────────────────────────────────────────────
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function el(tag, cls, html) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

// ── Storage ───────────────────────────────────────────────────────────────────
function loadLogs()     { try { logs     = JSON.parse(localStorage.getItem(SL_KEY)      || '[]'); } catch { logs = []; } }
function saveLogs()     { localStorage.setItem(SL_KEY, JSON.stringify(logs)); syncToCloud(); }
function loadRoutines() { try { routines = JSON.parse(localStorage.getItem(ROUTINE_KEY) || '[]'); } catch { routines = []; } }
function saveRoutines() { localStorage.setItem(ROUTINE_KEY, JSON.stringify(routines)); syncToCloud(); }
function loadGoals()    { try { goals    = JSON.parse(localStorage.getItem(GOALS_KEY)   || '{}'); } catch { goals = {}; } }
function saveGoals()    { localStorage.setItem(GOALS_KEY, JSON.stringify(goals)); syncToCloud(); }

function loadPrefs() {
  try { prefs = { ...prefs, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') }; }
  catch { /* keep defaults */ }
}
function savePrefs() { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); syncToCloud(); }

function loadActive() {
  try { return JSON.parse(localStorage.getItem(ACTIVE_KEY) || 'null'); }
  catch { return null; }
}
function saveActive(state) {
  if (state) localStorage.setItem(ACTIVE_KEY, JSON.stringify(state));
  else localStorage.removeItem(ACTIVE_KEY);
}

// ── Cloud sync (birch_tracker.sleep metafield) ────────────────────────────────
const CLOUD_API  = '/apps/ai/tracker/sleep';
const TRIM_MS    = 30 * 24 * 60 * 60 * 1000;
let _syncTimer   = null;

function _cloudPayload() {
  const cutoff = Date.now() - TRIM_MS;
  return {
    logs:      logs.filter(l => l.endTime >= cutoff),
    routines:  routines.filter(r => r.endTime >= cutoff),
    goals,
    prefs,
    updatedAt: Date.now(),
  };
}

function syncToCloud() {
  clearTimeout(_syncTimer);
  _syncTimer = setTimeout(() => {
    fetch(CLOUD_API, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(_cloudPayload()),
    }).catch(() => {});
  }, 400);
}

async function loadFromCloud() {
  try {
    const r = await fetch(CLOUD_API, { credentials: 'same-origin' });
    if (!r.ok) return;
    const { data } = await r.json();
    if (!data || typeof data !== 'object') return;
    if (Array.isArray(data.logs))     { logs     = data.logs;     localStorage.setItem(SL_KEY,       JSON.stringify(logs));     }
    if (Array.isArray(data.routines)) { routines = data.routines; localStorage.setItem(ROUTINE_KEY,  JSON.stringify(routines)); }
    if (data.goals && typeof data.goals === 'object') { goals = data.goals; localStorage.setItem(GOALS_KEY, JSON.stringify(goals)); }
    if (data.prefs && typeof data.prefs === 'object') { prefs = { ...prefs, ...data.prefs }; localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); }
  } catch {}
}

// ── Age & stage ───────────────────────────────────────────────────────────────
function getAgeWeeks() {
  if (!prefs.birthdate) return 0;
  const birth  = new Date(prefs.birthdate);
  const adjust = prefs.gestationalAge ? Math.max(0, 40 - prefs.gestationalAge) : 0;
  birth.setDate(birth.getDate() + adjust * 7);
  return Math.floor((Date.now() - birth.getTime()) / (7 * 24 * 3600 * 1000));
}

function getAgeMonths() {
  if (!prefs.birthdate) return 0;
  const birth  = new Date(prefs.birthdate);
  const adjust = prefs.gestationalAge ? Math.max(0, 40 - prefs.gestationalAge) : 0;
  birth.setDate(birth.getDate() + adjust * 7);
  const now = new Date();
  return (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
}

function getStage() {
  const m = getAgeMonths();
  for (const s of STAGES) if (m >= s.minMonths && m < s.maxMonths) return s.id;
  return m < 0 ? 1 : 5;
}

function getStageLabel() {
  return STAGES.find(s => s.id === getStage())?.label || '';
}

function getWakeWindow() {
  const weeks = getAgeWeeks();
  return WAKE_WINDOWS.find(w => weeks < w.maxWeeks) || WAKE_WINDOWS[WAKE_WINDOWS.length - 1];
}

// ── Time utilities ────────────────────────────────────────────────────────────
function msToHHMM(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  return `${String(h).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function formatDuration(min) {
  if (!min) return '0m';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatTimeAgo(ts) {
  const diff = Date.now() - ts;
  const m = Math.round(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h < 24) return rem === 0 ? `${h}h ago` : `${h}h ${rem}m ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDate(ts) {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
}

function newId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

// ── Log helpers ───────────────────────────────────────────────────────────────
function todayLogs() {
  const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
  return logs.filter(l => l.startTime >= midnight.getTime());
}

function lastLog() {
  return logs.length ? logs[logs.length - 1] : null;
}

function inferSleepType(startTs) {
  const h = new Date(startTs).getHours();
  return (h >= 19 || h < 6) ? 'night' : 'nap';
}

// ── Navigation ────────────────────────────────────────────────────────────────
function navigate(name, options) {
  var pushHistory = !options || options.pushHistory !== false;
  var prev = screen;
  screen = name;
  $$('.dt-screen').forEach(s => s.classList.remove('dt-screen--active'));
  $$('.dt-nav-item').forEach(b => b.classList.remove('dt-nav-item--active'));

  const scr = $(`#dt-screen-${name}`);
  if (scr) scr.classList.add('dt-screen--active');

  const btn = $(`.dt-nav-item[data-screen="${name}"]`);
  if (btn) btn.classList.add('dt-nav-item--active');

  if (name === 'home')     renderHome();
  if (name === 'schedule') renderSchedule();
  if (name === 'history')  renderHistory();
  if (name === 'insights') renderInsights();

  if (pushHistory && name !== 'home' && prev !== name) {
    history.pushState({ dtScreen: name }, '');
  }
}

window.addEventListener('popstate', function(e) {
  if (e.state && e.state.dtScreen) {
    navigate(e.state.dtScreen, { pushHistory: false });
  } else if (screen !== 'home') {
    navigate('home', { pushHistory: false });
  }
});

function openOverlay(id) { const o = $(`#${id}`); if (o) o.classList.add('dt-overlay--active'); }
function closeOverlay(id) { const o = $(`#${id}`); if (o) o.classList.remove('dt-overlay--active'); }

// ── Toast ─────────────────────────────────────────────────────────────────────
function showToast(msg, duration = 2800) {
  let toast = $('#dt-toast');
  if (!toast) {
    toast = el('div', 'dt-toast'); toast.id = 'dt-toast';
    document.body.appendChild(toast);
  }
  toast.textContent = msg;
  toast.classList.add('dt-toast--show');
  clearTimeout(toast._t);
  toast._t = setTimeout(() => toast.classList.remove('dt-toast--show'), duration);
}

// ── Onboarding ────────────────────────────────────────────────────────────────
let _sleepAllContexts = [];
let _sleepCtx = { childName: null, childId: null, ownerId: null, type: 'own' };

async function hydrateFromSharedProfile() {
  if (!window.BirchChildren) return prefs.onboardingDone;
  try {
    const BC  = window.BirchChildren;
    const BTC = window.BirchCaregivers;
    const allCtxs = BTC ? await BTC.getAllContexts('sleep') : (await BC.load()).map(c => ({
      type: 'own', childName: c.name, childId: c.id, dob: c.dob, ownerId: null, ownerName: null, label: c.name
    }));
    _sleepAllContexts = allCtxs;

    if (allCtxs.length === 0) return false;

    // Auto-select if only one child; keep existing selection otherwise
    let ctx = allCtxs.length === 1 ? allCtxs[0] : allCtxs[0];
    _sleepCtx = ctx;
    prefs.babyName       = ctx.childName || prefs.babyName;
    prefs.birthdate      = ctx.dob       || prefs.birthdate;
    prefs.onboardingDone = true;
    savePrefs();
    updateChildSelectorLabel();
    return true;
  } catch (e) { return false; }
}

function updateChildSelectorLabel() {
  const el = $('#dt-child-selector-label');
  if (el) el.textContent = _sleepCtx.childName || prefs.babyName || 'Select child';
}

async function switchSleepChild() {
  if (!window.BirchChildren || _sleepAllContexts.length < 2) return;
  const ctx = await window.BirchChildren.pickContext(_sleepAllContexts, { title: 'Whose sleep are you tracking?' });
  if (!ctx) return;
  _sleepCtx       = ctx;
  prefs.babyName  = ctx.childName || '';
  prefs.birthdate = ctx.dob || prefs.birthdate;
  savePrefs();
  updateChildSelectorLabel();
  renderHome();
  if (screen === 'insights') renderInsights();
}

function checkOnboarding() {
  if (!prefs.onboardingDone) openOverlay('dt-onboarding');
}

function saveOnboarding() {
  const name  = $('#dt-ob-name')?.value.trim() || '';
  const birth = $('#dt-ob-birthdate')?.value || '';
  const gest  = parseInt($('#dt-ob-gestational')?.value || '0') || null;
  const sit   = $('.dt-ob-sit--active')?.dataset.sit || 'ok';

  if (!birth) { showToast('Please enter baby\'s date of birth.'); return; }

  prefs.babyName       = name;
  prefs.birthdate      = birth;
  prefs.gestationalAge = gest;
  prefs.sleepSituation = sit;
  prefs.onboardingDone = true;
  savePrefs();

  // Sync to shared profile
  if (window.BirchChildren) {
    window.BirchChildren.upsert({ id: window.BirchChildren.newId(), name: name || 'Baby', dob: birth, gestationalAge: gest || undefined }).catch(function(){});
  }

  closeOverlay('dt-onboarding');
  applyStageUI(getStage());
  updateTopbar();
  renderHome();
}

// ── Stage UI ──────────────────────────────────────────────────────────────────
function applyStageUI(stage) {
  const show = (id, visible) => { const e = $(`#${id}`); if (e) e.style.display = visible ? '' : 'none'; };

  // Quick-log row
  show('dt-ql-sleep',   stage <= 3);
  show('dt-ql-nap',     stage >= 4);
  show('dt-ql-night',   stage >= 4);
  show('dt-ql-routine', stage >= 3);
  show('dt-ql-wakings', stage >= 4);

  // Routine history filter
  const routineChip = $('.dt-filter-chip[data-filter="routine"]');
  if (routineChip) routineChip.style.display = stage >= 3 ? '' : 'none';

  // Stage badge — hide for Newborn (stage 1) as it adds little value
  const badge = $('.dt-topbar__stage');
  if (badge) {
    const stageLabel = getStageLabel();
    badge.textContent = stageLabel;
    badge.style.display = (prefs.onboardingDone && getStage() > 1) ? '' : 'none';
  }

  // Nap 2 goals row — hide for stage 4-5 (single nap)
  const nap2Row = $('#dt-goal-nap2-row');
  if (nap2Row) nap2Row.style.display = stage <= 3 ? '' : 'none';
}

function updateTopbar() {
  const hour = new Date().getHours();
  const greet = $('.dt-topbar__greeting');
  if (greet) greet.textContent = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  const nameEl = $('.dt-topbar__name');
  const baby = prefs.babyName || _sleepCtx.childName || '';
  if (nameEl) nameEl.textContent = baby ? `${baby}'s sleep log` : 'Sleep log';

  updateChildSelectorLabel();
}

// ── Sleep timer — drift-proof ─────────────────────────────────────────────────
function startSleep(type) {
  if (activeSleep) { showToast('Sleep already in progress.'); return; }
  const inferredType = type || inferSleepType(Date.now());
  activeSleep = { id: newId(), type: inferredType, startTime: Date.now() };
  saveActive(activeSleep);
  startTimerTick();
  closeOverlay('dt-log-sheet');
  renderHome();
  showToast(`${inferredType === 'night' ? 'Night sleep' : 'Nap'} started!`);
}

function endSleep(opts = {}) {
  if (!activeSleep) return;
  if (slTimerHandle) { clearInterval(slTimerHandle); slTimerHandle = null; }

  const endTime    = Date.now();
  const durationMin = Math.round((endTime - activeSleep.startTime) / 60000);

  if (durationMin < 1) {
    saveActive(null);
    activeSleep = null;
    closeOverlay('dt-log-sheet');
    showToast('Too short — not saved.');
    renderHome();
    return;
  }

  const type = opts.type || activeSleep.type;
  const today = todayLogs();
  const napNumber = today.filter(l => l.type === 'nap').length + (type === 'nap' ? 1 : 0);

  const log = {
    id:           activeSleep.id,
    type,
    startTime:    activeSleep.startTime,
    endTime,
    durationMin,
    napNumber:    type === 'nap' ? napNumber : null,
    location:     opts.location || null,
    method:       opts.method   || null,
    settleMin:    opts.settleMin > 0 ? opts.settleMin : null,
    quality:      opts.quality  || null,
    nightWakings: opts.nightWakings || null,
    notes:        opts.notes    || '',
    source:       'timer'
  };

  logs.push(log);
  saveLogs();
  saveActive(null);
  activeSleep = null;

  closeOverlay('dt-log-sheet');
  showToast(`${type === 'night' ? 'Night sleep' : 'Nap'} saved! ${formatDuration(durationMin)}`);
  renderHome();
  checkRegressionCards();
}

function startTimerTick() {
  if (slTimerHandle) clearInterval(slTimerHandle);
  slTimerHandle = setInterval(() => {
    if (!activeSleep) { clearInterval(slTimerHandle); return; }
    const elapsed = Date.now() - activeSleep.startTime;
    const timerEl = $('#dt-timer-display');
    if (timerEl) timerEl.textContent = msToHHMM(elapsed);
    const infoEl = $('#dt-log-active-info');
    if (infoEl) infoEl.textContent = `${activeSleep.type === 'night' ? 'Night sleep' : 'Nap'} · ${msToHHMM(elapsed)}`;
  }, 1000);
}

// ── Home screen ───────────────────────────────────────────────────────────────
function renderHome() {
  const stage = getStage();
  renderHeroArea(stage);
  renderContextCards(stage);
  renderSummaryCards();
}

function renderHeroArea(stage) {
  const area = $('#dt-hero-area');
  if (!area) return;
  area.innerHTML = '';

  if (activeSleep) {
    renderSleepingHero(area);
  } else if (stage <= 2) {
    renderWakeWindowHero(area);
  } else {
    renderScheduleHero(area, stage);
  }
}

function renderSleepingHero(area) {
  const elapsed = Date.now() - activeSleep.startTime;
  const typeLabel = activeSleep.type === 'night' ? 'Night sleep' : 'Nap';
  area.innerHTML = `
    <div class="dt-hero dt-hero--sleeping">
      <div class="dt-hero__label">NOW SLEEPING</div>
      <div class="dt-hero__timer" id="dt-timer-display">${msToHHMM(elapsed)}</div>
      <div class="dt-hero__timer-sub">${typeLabel} in progress</div>
      <button class="dt-hero__end-btn" id="dt-hero-end">End sleep</button>
    </div>`;
  $('#dt-hero-end')?.addEventListener('click', () => openLogSheet());
  startTimerTick();
}

function renderWakeWindowHero(area) {
  const lastSleep = lastLog();
  const lastEndTs = lastSleep?.endTime || null;
  const awakeMs   = lastEndTs ? (Date.now() - lastEndTs) : null;
  const ww        = getWakeWindow();

  let indicator = 'green';
  if (awakeMs !== null) {
    const awakeMin = awakeMs / 60000;
    if (awakeMin >= ww.max) indicator = 'red';
    else if (awakeMin >= ww.min) indicator = 'amber';
  }

  const awakeStr = awakeMs !== null ? formatDuration(Math.round(awakeMs / 60000)) : '—';

  area.innerHTML = `
    <div class="dt-hero dt-hero--awake dt-hero--ww-${indicator}">
      <div class="dt-hero__label">AWAKE</div>
      <div class="dt-hero__ww-time">${awakeStr}</div>
      <div class="dt-hero__ww-target">Wake window: ${ww.label}</div>
      ${indicator === 'amber' ? `<div class="dt-hero__ww-tip">${prefs.babyName || 'Baby'} may be getting tired — watch for sleepy cues.</div>` : ''}
      ${indicator === 'red'   ? `<div class="dt-hero__ww-tip">Past the wake window — sleep when ready.</div>` : ''}
      <button class="dt-hero__sleep-btn" id="dt-hero-start-sleep">Baby's sleeping</button>
    </div>`;
  $('#dt-hero-start-sleep')?.addEventListener('click', () => startSleep());
}

function renderScheduleHero(area, stage) {
  const today    = todayLogs();
  const naps     = today.filter(l => l.type === 'nap');
  const nightLog = today.find(l => l.type === 'night');
  const lastSleep = lastLog();
  const awakeMs   = lastSleep?.endTime ? (Date.now() - lastSleep.endTime) : null;
  const ww        = getWakeWindow();

  // Bedtime target prompt (stage 3+, after 6pm)
  const hour = new Date().getHours();
  const bedtimeTarget = goals.targetBedtime || null;
  let bedtimeStr = '';
  if (bedtimeTarget && hour >= 16) {
    const [bh, bm] = bedtimeTarget.split(':').map(Number);
    const now = new Date();
    const bedTs = new Date(now.getFullYear(), now.getMonth(), now.getDate(), bh, bm);
    const diffMin = Math.round((bedTs.getTime() - now.getTime()) / 60000);
    if (diffMin > 0) bedtimeStr = `<div class="dt-hero__bedtime-target">Bedtime target: ${bedtimeTarget} · ${diffMin} min away</div>`;
    else if (diffMin > -30) bedtimeStr = `<div class="dt-hero__bedtime-target dt-hero__bedtime-target--now">Bedtime now</div>`;
  }

  const napCountLabel = stage >= 4 ? (naps.length >= 1 ? '1 nap ✓' : '0 naps') : `${naps.length} nap${naps.length !== 1 ? 's' : ''}`;

  area.innerHTML = `
    <div class="dt-hero dt-hero--schedule">
      <div class="dt-hero__label">TODAY</div>
      <div class="dt-hero__schedule-row">
        <div class="dt-hero__sched-item">
          <div class="dt-hero__sched-val">${napCountLabel}</div>
          <div class="dt-hero__sched-sub">Naps logged</div>
        </div>
        ${nightLog ? `<div class="dt-hero__sched-item">
          <div class="dt-hero__sched-val">${formatDuration(nightLog.durationMin)}</div>
          <div class="dt-hero__sched-sub">Night sleep</div>
        </div>` : ''}
        ${awakeMs !== null ? `<div class="dt-hero__sched-item">
          <div class="dt-hero__sched-val">${formatDuration(Math.round(awakeMs / 60000))}</div>
          <div class="dt-hero__sched-sub">Awake · target ${ww.label}</div>
        </div>` : ''}
      </div>
      ${bedtimeStr}
      <button class="dt-hero__sleep-btn" id="dt-hero-start-sleep">Baby's sleeping</button>
    </div>`;
  $('#dt-hero-start-sleep')?.addEventListener('click', () => startSleep());
}

function renderContextCards(stage) {
  const area = $('#dt-context-cards');
  if (!area) return;
  area.innerHTML = '';

  // Regression card
  const regression = getActiveRegression();
  if (regression && !prefs.regressionCardsSeen?.includes(regression.key)) {
    const card = el('div', 'dt-context-card dt-context-card--regression');
    card.innerHTML = `
      <div class="dt-context-card__title">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        ${regression.label}
      </div>
      <div class="dt-context-card__body">${prefs.babyName || 'Baby'} is in the window for the ${regression.label.toLowerCase()}. ${regression.guide}</div>
      <div class="dt-context-card__actions">
        <button class="dt-context-card__dismiss" data-regression="${regression.key}">Got it</button>
      </div>`;
    area.appendChild(card);
    card.querySelector('.dt-context-card__dismiss')?.addEventListener('click', (e) => {
      if (!prefs.regressionCardsSeen) prefs.regressionCardsSeen = [];
      prefs.regressionCardsSeen.push(e.target.dataset.regression);
      savePrefs();
      e.target.closest('.dt-context-card').remove();
    });
  }

  // Nap transition card
  const transition = getNapTransitionReadiness(stage);
  if (transition) {
    const card = el('div', 'dt-context-card dt-context-card--transition');
    card.innerHTML = `
      <div class="dt-context-card__title">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22V12m0-8v4M4.93 4.93l2.83 2.83M17.07 4.93l-2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83M17.07 19.07l-2.83-2.83"/></svg>
        ${transition.title}
      </div>
      <div class="dt-context-card__body">${transition.body}</div>
      <div class="dt-context-card__actions">
        <button class="dt-context-card__cta" id="dt-transition-guide-btn">Here's how</button>
        <button class="dt-context-card__dismiss dt-transition-dismiss">Maybe later</button>
      </div>`;
    area.appendChild(card);
    $('#dt-transition-guide-btn')?.addEventListener('click', () => openNapGuide(transition));
    card.querySelector('.dt-transition-dismiss')?.addEventListener('click', () => {
      prefs[`transition_${transition.key}_dismissed`] = true;
      savePrefs();
      card.remove();
    });
  }

  // Bedtime routine prompt (stage 3+, 6–9pm, no routine tonight)
  if (stage >= 3) {
    const h = new Date().getHours();
    const tonightRoutine = routines.find(r => {
      const d = new Date(r.startTime);
      const today = new Date();
      return d.toDateString() === today.toDateString();
    });
    if (h >= 18 && h < 21 && !tonightRoutine && !activeSleep) {
      const card = el('div', 'dt-context-card dt-context-card--routine-prompt');
      card.innerHTML = `
        <div class="dt-context-card__title">Bedtime routine for ${prefs.babyName || 'baby'}?</div>
        <div class="dt-context-card__actions">
          <button class="dt-context-card__cta" id="dt-home-routine-start">Start timer</button>
          <button class="dt-context-card__dismiss" id="dt-home-routine-dismiss">Not tonight</button>
        </div>`;
      area.appendChild(card);
      $('#dt-home-routine-start')?.addEventListener('click', () => openRoutineOverlay());
      $('#dt-home-routine-dismiss')?.addEventListener('click', () => card.remove());
    }
  }

  // Feed-to-sleep detection (stage 2–4)
  if (stage >= 2 && stage <= 4) {
    const fts = detectFeedToSleep();
    if (fts && !prefs.feedToSleepCardSeen) {
      const card = el('div', 'dt-context-card dt-context-card--fts');
      card.innerHTML = `
        <div class="dt-context-card__title">Pattern noticed</div>
        <div class="dt-context-card__body">${prefs.babyName || 'Baby'} usually falls asleep within 10 minutes of a feed — ${fts.count} of the last 10 naps followed this pattern. This is very common. If you'd ever like to explore other settle methods, you can track what works here.</div>
        <div class="dt-context-card__actions">
          <button class="dt-context-card__dismiss" id="dt-fts-dismiss">Got it</button>
        </div>`;
      area.appendChild(card);
      $('#dt-fts-dismiss')?.addEventListener('click', () => {
        prefs.feedToSleepCardSeen = true;
        savePrefs();
        card.remove();
      });
    }
  }
}

function renderSummaryCards() {
  const today   = todayLogs();
  const totalMin = today.reduce((a, l) => a + (l.durationMin || 0), 0);
  const naps    = today.filter(l => l.type === 'nap');
  const longest = Math.max(0, ...today.map(l => l.durationMin || 0));
  const last    = lastLog();

  const sumTotal  = $('#dt-sum-total');
  if (sumTotal) sumTotal.innerHTML = totalMin > 0 ? `<span class="dt-sum-value">${formatDuration(totalMin)}</span>` : `<span class="dt-sum-value" style="color:var(--dt-muted)">—</span>`;

  const sumLongest = $('#dt-sum-longest');
  if (sumLongest) sumLongest.innerHTML = longest > 0 ? `<span class="dt-sum-value">${formatDuration(longest)}</span>` : `<span class="dt-sum-value" style="color:var(--dt-muted)">—</span>`;

  const sumNaps = $('#dt-sum-naps');
  if (sumNaps) sumNaps.innerHTML = `<span class="dt-sum-value">${naps.length}</span>`;

  const sumLast = $('#dt-sum-last');
  if (sumLast) {
    if (last?.endTime) sumLast.innerHTML = `<span class="dt-sum-value" style="font-size:1.05rem">${formatTime(last.endTime)}</span>`;
    else sumLast.innerHTML = `<span class="dt-sum-value" style="color:var(--dt-muted)">—</span>`;
  }
}

// ── Log sheet ─────────────────────────────────────────────────────────────────
function openLogSheet() {
  const active = loadActive();
  activeSleep  = active;

  const startDiv = $('#dt-log-start');
  const activeDiv = $('#dt-log-active');

  settleVal  = 0;
  wakingsVal = 0;
  const settleEl = $('#dt-settle-val');  if (settleEl) settleEl.textContent = '0';
  const wakEl    = $('#dt-wakings-val'); if (wakEl)    wakEl.textContent    = '0';

  if (active) {
    if (startDiv) startDiv.style.display  = 'none';
    if (activeDiv) activeDiv.style.display = '';
    // Infer type
    const inferredType = active.type || inferSleepType(active.startTime);
    $$('.dt-log-type-btn').forEach(b => b.classList.toggle('dt-log-type-btn--active', b.dataset.ltype === inferredType));

    const nightGroup = $('#dt-log-night-wakings-group');
    if (nightGroup) nightGroup.style.display = inferredType === 'night' ? '' : 'none';

    $('#dt-log-sheet-title').textContent = 'End sleep session';

    // Update info
    const elapsed = Date.now() - active.startTime;
    const infoEl = $('#dt-log-active-info');
    if (infoEl) infoEl.textContent = `${active.type === 'night' ? 'Night sleep' : 'Nap'} · ${msToHHMM(elapsed)}`;

    // Reset detail pills
    $$('.dt-log-pill').forEach(p => p.classList.remove('dt-log-pill--active'));
    const detailsEl = $('#dt-log-details');
    if (detailsEl) detailsEl.style.display = 'none';
    const toggleEl = $('#dt-log-details-toggle');
    if (toggleEl) toggleEl.classList.remove('dt-log-details-toggle--open');
  } else {
    if (startDiv) startDiv.style.display  = '';
    if (activeDiv) activeDiv.style.display = 'none';
    $('#dt-log-sheet-title').textContent = 'Log sleep';
  }

  openOverlay('dt-log-sheet');
}

function endSleepFromSheet() {
  const type       = $('.dt-log-type-btn--active')?.dataset.ltype || inferSleepType(activeSleep?.startTime || Date.now());
  const location   = $('.dt-log-pill[data-location].dt-log-pill--active')?.dataset.location || null;
  const method     = $('.dt-log-pill[data-method].dt-log-pill--active')?.dataset.method    || null;
  const quality    = $('.dt-log-pill[data-quality].dt-log-pill--active')?.dataset.quality  || null;
  const notes      = $('#dt-log-notes')?.value.trim() || '';

  endSleep({ type, location, method, settleMin: settleVal, quality, nightWakings: type === 'night' ? wakingsVal : null, notes });
}

// ── Regression detection ──────────────────────────────────────────────────────
function getActiveRegression() {
  const weeks = getAgeWeeks();
  for (const r of REGRESSIONS) {
    if (weeks >= r.minWeeks && weeks <= r.maxWeeks) return r;
  }
  return null;
}

function checkRegressionCards() {
  const r = getActiveRegression();
  if (!r) return;
  if (!prefs.regressionCardsSeen) prefs.regressionCardsSeen = [];
  // Already checked via context cards on home render
}

// ── Nap transition readiness ──────────────────────────────────────────────────
function getNapTransitionReadiness(stage) {
  const months = getAgeMonths();

  // 3→2 transition (stage 3: 6–8 months)
  if (stage === 3 && months >= 6 && months <= 10 && !prefs.transition_3to2_dismissed && !prefs.transition_3to2_done) {
    const last14 = logs.filter(l => l.startTime >= Date.now() - 14 * 86400000);
    const thirdNapsShort = last14.filter(l => l.type === 'nap' && l.napNumber === 3 && l.durationMin < 30).length;
    if (thirdNapsShort >= 5) {
      return {
        key: '3to2',
        title: `${prefs.babyName || 'Baby'} may be ready to drop the third nap`,
        body: 'The third nap has been consistently short. This often signals readiness for 2 naps.',
        guide: [
          { h: 'What to expect', p: 'The first week may bring earlier fussiness and a temporarily earlier bedtime. This is normal.' },
          { h: 'Adjust wake windows', p: 'Slightly lengthen the gap between nap 2 and bedtime to fill the space the third nap used to occupy.' },
          { h: 'If it\'s not working', p: 'Try again in 2–3 weeks. There\'s no rush — both timings can work.' }
        ]
      };
    }
  }

  // 2→1 transition (stage 4: 13–18 months)
  if (stage === 4 && months >= 13 && months <= 18 && !prefs.transition_2to1_dismissed && !prefs.transition_2to1_done) {
    const last14 = logs.filter(l => l.startTime >= Date.now() - 14 * 86400000);
    const shortNaps = last14.filter(l => l.type === 'nap' && l.durationMin < 30).length;
    const lateBedtimes = routines.filter(r => {
      if (r.startTime < Date.now() - 10 * 86400000) return false;
      const h = new Date(r.startTime).getHours();
      return (h >= 21 || h < 5);
    }).length;
    if (shortNaps >= 6 || lateBedtimes >= 4) {
      return {
        key: '2to1',
        title: `${prefs.babyName || 'Baby'} may be ready for one nap`,
        body: `${prefs.babyName || 'Baby'}'s naps have been consistently short. The 2-to-1 nap transition typically happens between 13–18 months.`,
        guide: [
          { h: 'Start with one long nap', p: 'Aim for a nap starting between 11:30am and 12:30pm, lasting 1–2 hours.' },
          { h: 'Expect an earlier bedtime', p: 'Move bedtime 30–60 minutes earlier for the first 2–3 weeks while wake windows adjust.' },
          { h: 'Watch for overtiredness', p: 'On hard days, an early nap is fine. Transition takes 2–6 weeks.' }
        ]
      };
    }
  }

  return null;
}

function openNapGuide(transition) {
  const header = $('#dt-nap-guide-header');
  if (header) header.textContent = transition.title;

  const cardsEl = $('#dt-nap-guide-cards');
  if (cardsEl) {
    cardsEl.innerHTML = transition.guide.map((c, i) => `
      <div class="dt-nap-guide-card">
        <div class="dt-nap-guide-card__num">${i + 1}</div>
        <div>
          <div class="dt-nap-guide-card__h">${c.h}</div>
          <div class="dt-nap-guide-card__p">${c.p}</div>
        </div>
      </div>`).join('');
  }

  openOverlay('dt-overlay-nap-guide');
}

// ── Feed-to-sleep detection (cross-ref NurseNote) ─────────────────────────────
function detectFeedToSleep() {
  try {
    const ftSessions = JSON.parse(localStorage.getItem('ft_sessions') || '[]');
    const recentLogs  = logs.filter(l => l.startTime >= Date.now() - 14 * 86400000 && l.type !== 'night');
    if (recentLogs.length < 8) return null;

    let matchCount = 0;
    const last10 = recentLogs.slice(-10);
    last10.forEach(nap => {
      const feedBefore = ftSessions.find(f => f.endedAt && Math.abs(nap.startTime - f.endedAt) <= 10 * 60000 && f.endedAt < nap.startTime);
      if (feedBefore) matchCount++;
    });
    if (matchCount / last10.length > 0.7) return { count: matchCount };
  } catch {}
  return null;
}

// ── BEDTIME ROUTINE ───────────────────────────────────────────────────────────
function openRoutineOverlay() {
  routineStartTs = null;
  if (routineTimer) { clearInterval(routineTimer); routineTimer = null; }

  const startWrap  = $('#dt-routine-start-wrap');
  const activeWrap = $('#dt-routine-active-wrap');
  if (startWrap)  startWrap.style.display  = '';
  if (activeWrap) activeWrap.style.display = 'none';

  $$('.dt-routine-step').forEach(b => b.classList.remove('dt-routine-step--active'));
  $$('#dt-routine-mood-pills .dt-log-pill').forEach(b => b.classList.remove('dt-log-pill--active'));

  const endBtn = $('#dt-routine-end');
  if (endBtn) endBtn.style.display = 'none';

  openOverlay('dt-overlay-routine');
}

function startRoutineTimer() {
  routineStartTs = Date.now();
  const startWrap  = $('#dt-routine-start-wrap');
  const activeWrap = $('#dt-routine-active-wrap');
  if (startWrap)  startWrap.style.display  = 'none';
  if (activeWrap) activeWrap.style.display = '';

  const endBtn = $('#dt-routine-end');
  if (endBtn) endBtn.style.display = '';

  routineTimer = setInterval(() => {
    const elapsed = Date.now() - routineStartTs;
    const timerEl = $('#dt-routine-timer');
    const m = Math.floor(elapsed / 60000);
    const s = Math.floor((elapsed % 60000) / 1000);
    if (timerEl) timerEl.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }, 1000);
}

function endRoutine() {
  if (!routineStartTs) return;
  if (routineTimer) { clearInterval(routineTimer); routineTimer = null; }

  const endTime     = Date.now();
  const durationMin = Math.round((endTime - routineStartTs) / 60000);
  const steps       = [...$$('.dt-routine-step--active')].map(b => b.dataset.step);
  const mood        = $('.dt-log-pill[data-mood].dt-log-pill--active')?.dataset.mood || null;

  const routine = { id: newId(), startTime: routineStartTs, endTime, durationMin, steps, babyMood: mood };
  routines.push(routine);
  saveRoutines();

  routineStartTs = null;
  closeOverlay('dt-overlay-routine');
  showToast(`Routine saved — ${durationMin} min`);
  renderHome();
}

// ── NIGHT WAKINGS QUICK LOG ───────────────────────────────────────────────────
function openWakingsSheet() {
  wkVal = 0;
  const wkEl = $('#dt-wk-val');
  if (wkEl) wkEl.textContent = '0';
  openOverlay('dt-overlay-wakings');
}

function saveNightWakings() {
  const lastNight = [...logs].reverse().find(l => l.type === 'night');
  if (!lastNight) {
    showToast('No night sleep logged to attach wakings to.');
    closeOverlay('dt-overlay-wakings');
    return;
  }
  lastNight.nightWakings = wkVal;
  saveLogs();
  closeOverlay('dt-overlay-wakings');
  showToast(`${wkVal} waking${wkVal !== 1 ? 's' : ''} logged for last night.`);
  renderHome();
}

// ── SCHEDULE VIEW ─────────────────────────────────────────────────────────────
function renderSchedule(view = 'day') {
  const container = $('#dt-schedule-content');
  if (!container) return;

  if (view === 'day') {
    renderDaySchedule(container);
  } else {
    renderWeekSchedule(container);
  }
}

function renderDaySchedule(container) {
  const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
  const todayStart = midnight.getTime();
  const todayEnd   = todayStart + 24 * 3600000;
  const todayItems = logs.filter(l => l.startTime < todayEnd && (l.endTime || Date.now()) > todayStart);

  container.innerHTML = `<div class="dt-schedule-day" id="dt-day-timeline"></div>`;
  const timeline = $('#dt-day-timeline');
  if (!timeline) return;

  // 24h pixel height = 576px (24px per hour)
  timeline.innerHTML = '';

  // Hour markers
  for (let h = 0; h < 24; h++) {
    const marker = el('div', 'dt-schedule-hour');
    marker.style.top = `${h * 24}px`;
    marker.innerHTML = `<span>${h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`}</span>`;
    timeline.appendChild(marker);
  }

  // Sleep blocks
  todayItems.forEach(item => {
    const start = Math.max(item.startTime, todayStart);
    const end   = Math.min(item.endTime || Date.now(), todayEnd);
    const topPx  = ((start - todayStart) / 3600000) * 24;
    const heightPx = Math.max(((end - start) / 3600000) * 24, 4);

    const block = el('div', `dt-sleep-block dt-sleep-block--${item.type}`);
    block.style.top    = `${topPx}px`;
    block.style.height = `${heightPx}px`;
    block.title = `${item.type === 'night' ? 'Night' : 'Nap'} · ${formatDuration(item.durationMin)} · ${formatTime(item.startTime)}–${formatTime(item.endTime)}`;

    // Goal overlay
    if (item.type === 'nap' && item.napNumber === 1 && goals.targetNap1Start) {
      const [gh, gm] = goals.targetNap1Start.split(':').map(Number);
      const goalStartMs = new Date(new Date().setHours(gh, gm, 0, 0)).getTime();
      const goalHeight  = ((goals.targetNap1DurationMin || 60) / 60) * 24;
      const goalTop     = ((goalStartMs - todayStart) / 3600000) * 24;
      const goalOverlay = el('div', 'dt-goal-block');
      goalOverlay.style.top    = `${goalTop}px`;
      goalOverlay.style.height = `${goalHeight}px`;
      timeline.appendChild(goalOverlay);
    }

    timeline.appendChild(block);
  });

  // Current time line
  const nowTop = ((Date.now() - todayStart) / 3600000) * 24;
  const nowLine = el('div', 'dt-schedule-now');
  nowLine.style.top = `${Math.min(nowTop, 576)}px`;
  timeline.appendChild(nowLine);
}

function renderWeekSchedule(container) {
  container.innerHTML = '<div class="dt-week-grid" id="dt-week-grid"></div>';
  const grid = $('#dt-week-grid');
  if (!grid) return;

  for (let d = 6; d >= 0; d--) {
    const day = new Date(); day.setHours(0, 0, 0, 0); day.setDate(day.getDate() - d);
    const dayEnd = new Date(day); dayEnd.setDate(dayEnd.getDate() + 1);
    const dayItems = logs.filter(l => l.startTime < dayEnd.getTime() && (l.endTime || Date.now()) > day.getTime());

    const row = el('div', 'dt-week-row');
    const label = el('div', 'dt-week-label', d === 0 ? 'Today' : day.toLocaleDateString([], { weekday: 'short' }));
    const track = el('div', 'dt-week-track');

    dayItems.forEach(item => {
      const start  = Math.max(item.startTime, day.getTime());
      const end    = Math.min(item.endTime || Date.now(), dayEnd.getTime());
      const leftPct  = ((start - day.getTime()) / 86400000) * 100;
      const widthPct = Math.max(((end - start) / 86400000) * 100, 0.5);
      const block = el('div', `dt-week-block dt-week-block--${item.type}`);
      block.style.left  = `${leftPct}%`;
      block.style.width = `${widthPct}%`;
      block.title = `${formatDuration(item.durationMin)}`;
      track.appendChild(block);
    });

    row.appendChild(label);
    row.appendChild(track);
    grid.appendChild(row);
  }
}

// ── HISTORY ───────────────────────────────────────────────────────────────────
function renderHistory(filter = 'all') {
  const container = $('#dt-history-list');
  if (!container) return;

  const allItems = [
    ...logs.map(l => ({ ...l, _ts: l.endTime || l.startTime })),
    ...routines.map(r => ({ ...r, type: 'routine', _ts: r.endTime || r.startTime }))
  ].sort((a, b) => b._ts - a._ts);

  let list = allItems;
  if (filter === 'nap')     list = list.filter(i => i.type === 'nap');
  if (filter === 'night')   list = list.filter(i => i.type === 'night');
  if (filter === 'routine') list = list.filter(i => i.type === 'routine');

  if (!list.length) {
    container.innerHTML = '<div class="dt-empty-state"><p>No sleep logged yet.</p></div>';
    return;
  }

  const groups = {};
  list.forEach(i => {
    const key = formatDate(i._ts);
    if (!groups[key]) groups[key] = [];
    groups[key].push(i);
  });

  container.innerHTML = '';
  Object.entries(groups).forEach(([date, items]) => {
    const group = el('div', 'dt-history-group');
    group.innerHTML = `<div class="dt-history-date">${date}</div>`;
    items.forEach(item => {
      const row = el('div', 'dt-history-row');
      row.innerHTML = historyRowHTML(item);
      const del = row.querySelector('.dt-history-delete');
      if (del) del.addEventListener('click', () => deleteEntry(item.id, item.type));
      group.appendChild(row);
    });
    container.appendChild(group);
  });
}

function historyRowHTML(item) {
  const del = `<button class="dt-history-delete" title="Delete"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg></button>`;

  if (item.type === 'nap') {
    const method = item.method ? ` · ${item.method.replace('_', ' ')}` : '';
    return `<div class="dt-history-icon dt-history-icon--nap">💤</div><div class="dt-history-meta"><div class="dt-history-title">Nap${item.napNumber ? ` ${item.napNumber}` : ''} · ${formatDuration(item.durationMin)}</div><div class="dt-history-detail">${formatTime(item.startTime)}–${formatTime(item.endTime)}${method}</div></div><div class="dt-history-time">${formatTime(item._ts)}</div>${del}`;
  }
  if (item.type === 'night') {
    const wk = item.nightWakings != null ? ` · ${item.nightWakings} waking${item.nightWakings !== 1 ? 's' : ''}` : '';
    const quality = item.quality ? ` · ${item.quality.replace('_', ' ')}` : '';
    return `<div class="dt-history-icon dt-history-icon--night">🌙</div><div class="dt-history-meta"><div class="dt-history-title">Night sleep · ${formatDuration(item.durationMin)}</div><div class="dt-history-detail">${formatTime(item.startTime)}–${formatTime(item.endTime)}${wk}${quality}</div></div><div class="dt-history-time">${formatTime(item._ts)}</div>${del}`;
  }
  if (item.type === 'routine') {
    const steps = item.steps?.length ? ` · ${item.steps.join(', ')}` : '';
    const mood  = item.babyMood ? ` · ${item.babyMood}` : '';
    return `<div class="dt-history-icon dt-history-icon--routine">🌿</div><div class="dt-history-meta"><div class="dt-history-title">Bedtime routine · ${formatDuration(item.durationMin)}</div><div class="dt-history-detail">${formatTime(item.startTime)}–${formatTime(item.endTime)}${steps}${mood}</div></div><div class="dt-history-time">${formatTime(item._ts)}</div>${del}`;
  }
  return '';
}

function deleteEntry(id, type) {
  if (type === 'routine') {
    routines = routines.filter(r => r.id !== id);
    saveRoutines();
  } else {
    logs = logs.filter(l => l.id !== id);
    saveLogs();
  }
  renderHistory($('.dt-filter-chip--active')?.dataset.filter || 'all');
  renderHome();
}

// ── INSIGHTS ──────────────────────────────────────────────────────────────────
function renderInsights(period = '14d') {
  const container = $('#dt-insights-content');
  if (!container) return;

  const stage  = getStage();
  const now    = Date.now();
  const periodMs = period === '7d' ? 7 * 86400000 : period === '14d' ? 14 * 86400000 : 30 * 86400000;
  const days   = period === '7d' ? 7 : period === '14d' ? 14 : 30;
  const recent = logs.filter(l => l.startTime >= now - periodMs && l.endTime);

  if (!recent.length) {
    container.innerHTML = '<div class="dt-empty-state"><p>No data yet. Start logging sleep to see insights.</p></div>';
    return;
  }

  const naps   = recent.filter(l => l.type === 'nap');
  const nights = recent.filter(l => l.type === 'night');
  const totalMin = recent.reduce((a, l) => a + (l.durationMin || 0), 0);
  const avgPerDay = (totalMin / days).toFixed(0);
  const longestNight = Math.max(0, ...nights.map(l => l.durationMin || 0));
  const avgNapsPerDay = (naps.length / days).toFixed(1);
  const settleItems = recent.filter(l => l.settleMin);
  const avgSettle   = settleItems.length ? Math.round(settleItems.reduce((a, l) => a + l.settleMin, 0) / settleItems.length) : null;
  const avgWakings  = nights.filter(l => l.nightWakings != null).reduce((a, l, _, arr) => a + l.nightWakings / arr.length, 0);

  const narrative = buildNarrative({ recent, naps, nights, avgPerDay, longestNight, avgNapsPerDay, stage, days });

  let html = `
    <div class="dt-insights-narrative">${narrative}</div>
    <div class="dt-metrics-grid">
      <div class="dt-metric-card"><div class="dt-metric-value">${formatDuration(parseFloat(avgPerDay))}</div><div class="dt-metric-label">Avg. sleep/day</div></div>
      <div class="dt-metric-card"><div class="dt-metric-value">${avgNapsPerDay}</div><div class="dt-metric-label">Naps/day</div></div>
      <div class="dt-metric-card"><div class="dt-metric-value">${longestNight > 0 ? formatDuration(longestNight) : '—'}</div><div class="dt-metric-label">Longest night</div></div>
      <div class="dt-metric-card"><div class="dt-metric-value">${avgSettle !== null ? `${avgSettle}m` : '—'}</div><div class="dt-metric-label">Avg. settle time</div></div>
    </div>`;

  if (nights.length > 0 && avgWakings > 0) {
    html += `
      <div class="dt-insights-section">
        <div class="dt-insights-section__title">Night Wakings</div>
        <div class="dt-metrics-grid">
          <div class="dt-metric-card"><div class="dt-metric-value">${avgWakings.toFixed(1)}</div><div class="dt-metric-label">Avg. wakings/night</div></div>
          <div class="dt-metric-card"><div class="dt-metric-value">${nights.filter(l => (l.nightWakings || 0) === 0).length}</div><div class="dt-metric-label">Full nights (0 wakings)</div></div>
        </div>
      </div>`;
  }

  // Bar chart (daily sleep total)
  html += buildSleepBarChart(recent, days);

  // Schedule consistency (stage 3+)
  if (stage >= 3 && nights.length >= 3) {
    html += buildConsistencyInsight(nights);
  }

  container.innerHTML = html;
}

function buildNarrative({ recent, naps, nights, avgPerDay, longestNight, avgNapsPerDay, stage, days }) {
  const name = prefs.babyName || 'Baby';
  let text = `${name} has averaged <strong>${formatDuration(parseFloat(avgPerDay))}</strong> of total sleep per day over the last ${days} days, across ${naps.length} nap${naps.length !== 1 ? 's' : ''} and ${nights.length} night${nights.length !== 1 ? 's' : ''}.`;
  if (longestNight > 0) text += ` Longest night stretch: <strong>${formatDuration(longestNight)}</strong>.`;
  if (stage <= 2) text += ' Wake windows and sleep patterns are still forming — wide variation is completely normal.';
  else if (stage === 3) text += ' Nap consolidation often happens in this window — watch for a natural shift to 2 naps.';
  else if (stage >= 4) text += ` ${parseFloat(avgNapsPerDay) < 1.5 ? 'Single-nap schedule looks to be settling in.' : 'Two-nap schedule still active.'}`;
  return text;
}

function buildSleepBarChart(items, days) {
  const bars = [];
  for (let i = days - 1; i >= 0; i--) {
    const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0); dayStart.setDate(dayStart.getDate() - i);
    const dayEnd   = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
    const dayItems = items.filter(l => l.startTime >= dayStart.getTime() && l.startTime < dayEnd.getTime());
    const totalMin = dayItems.reduce((a, l) => a + (l.durationMin || 0), 0);
    bars.push({ label: i === 0 ? 'Today' : dayStart.toLocaleDateString([], { weekday: 'short' }), min: totalMin });
  }
  const maxMin = Math.max(...bars.map(b => b.min), 1);
  const barHTML = bars.map(b => {
    const pct = Math.round((b.min / maxMin) * 100);
    return `<div class="dt-bar-col"><div class="dt-bar-count">${b.min > 0 ? formatDuration(b.min) : ''}</div><div class="dt-bar-track"><div class="dt-bar-fill" style="height:${pct}%"></div></div><div class="dt-bar-label">${b.label}</div></div>`;
  }).join('');
  return `<div class="dt-bar-chart">${barHTML}</div>`;
}

function buildConsistencyInsight(nights) {
  if (!goals.targetBedtime || nights.length < 3) return '';
  const [th, tm] = goals.targetBedtime.split(':').map(Number);
  const targetMin = th * 60 + tm;
  const onTarget = nights.filter(l => {
    const h = new Date(l.startTime).getHours();
    const m = new Date(l.startTime).getMinutes();
    const actualMin = h * 60 + m;
    return Math.abs(actualMin - targetMin) <= 30;
  }).length;
  const pct = Math.round((onTarget / nights.length) * 100);
  return `
    <div class="dt-insights-section">
      <div class="dt-insights-section__title">Schedule Consistency</div>
      <div class="dt-metrics-grid">
        <div class="dt-metric-card"><div class="dt-metric-value">${pct}%</div><div class="dt-metric-label">Nights within 30 min of target</div></div>
        <div class="dt-metric-card"><div class="dt-metric-value">${goals.targetBedtime}</div><div class="dt-metric-label">Bedtime target</div></div>
      </div>
    </div>`;
}

// ── SETTINGS ──────────────────────────────────────────────────────────────────
function openSettings() {
  $('#dt-s-name')?.setAttribute('value', prefs.babyName || '');
  if ($('#dt-s-name')) $('#dt-s-name').value = prefs.babyName || '';
  if ($('#dt-s-birthdate')) $('#dt-s-birthdate').value = prefs.birthdate || '';
  if ($('#dt-s-gestational')) $('#dt-s-gestational').value = prefs.gestationalAge || '';
  if ($('#dt-g-bedtime')) $('#dt-g-bedtime').value = goals.targetBedtime || '';
  if ($('#dt-g-waketime')) $('#dt-g-waketime').value = goals.targetWakeTime || '';
  if ($('#dt-g-nap1start')) $('#dt-g-nap1start').value = goals.targetNap1Start || '';
  if ($('#dt-g-nap1dur')) $('#dt-g-nap1dur').value = goals.targetNap1DurationMin || '';
  if ($('#dt-g-nap2start')) $('#dt-g-nap2start').value = goals.targetNap2Start || '';
  if ($('#dt-g-nap2dur')) $('#dt-g-nap2dur').value = goals.targetNap2DurationMin || '';
  openOverlay('dt-overlay-settings');
}

function saveSettings() {
  prefs.babyName       = $('#dt-s-name')?.value.trim() || prefs.babyName;
  prefs.birthdate      = $('#dt-s-birthdate')?.value    || prefs.birthdate;
  const gest           = parseInt($('#dt-s-gestational')?.value || '0') || null;
  prefs.gestationalAge = gest;
  savePrefs();

  goals.targetBedtime        = $('#dt-g-bedtime')?.value   || null;
  goals.targetWakeTime       = $('#dt-g-waketime')?.value  || null;
  goals.targetNap1Start      = $('#dt-g-nap1start')?.value || null;
  goals.targetNap1DurationMin = parseInt($('#dt-g-nap1dur')?.value || '0') || null;
  goals.targetNap2Start      = $('#dt-g-nap2start')?.value || null;
  goals.targetNap2DurationMin = parseInt($('#dt-g-nap2dur')?.value || '0') || null;
  saveGoals();

  closeOverlay('dt-overlay-settings');
  applyStageUI(getStage());
  updateTopbar();
  renderHome();
  showToast('Settings saved.');
}

// ── QUICK LOG (no timer) ──────────────────────────────────────────────────────

function saveQuickSleep() {
  const hoursEl = $('#dt-quick-hours');
  const minsEl  = $('#dt-quick-minutes');
  const hours   = parseInt(hoursEl?.value || '0', 10) || 0;
  const mins    = parseInt(minsEl?.value  || '0', 10) || 0;
  const durationMin = hours * 60 + mins;

  if (durationMin <= 0) { showToast('Please enter a duration.'); return; }

  const type    = $('#dt-log-start .dt-log-type-btn--active')?.dataset.ltype || 'nap';
  const endTime = Date.now();
  const startTime = endTime - durationMin * 60000;

  logs.push({ id: newId(), type, startTime, endTime, durationMin, source: 'manual' });
  saveLogs();
  closeOverlay('dt-log-sheet');
  if (hoursEl) hoursEl.value = '';
  if (minsEl)  minsEl.value  = '';
  showToast('Sleep logged!');
  renderHome();
  if (screen === 'history') renderHistory();
}

// ── VOICE LOG ─────────────────────────────────────────────────────────────────

function parseSleepVoice(text) {
  const t = text.toLowerCase();
  // Type detection
  const type = /night|bedtime|overnight/.test(t) ? 'night' : 'nap';
  // Duration: "X hour(s) Y min(utes)" or "X min(utes)" or "X hour(s)"
  let durationMin = 0;
  const hrMatch  = t.match(/(\d+(?:\.\d+)?)\s*h(?:our|r)?s?/);
  const minMatch = t.match(/(\d+(?:\.\d+)?)\s*m(?:in(?:ute)?s?)?(?!\s*h)/);
  if (hrMatch)  durationMin += Math.round(parseFloat(hrMatch[1])  * 60);
  if (minMatch) durationMin += Math.round(parseFloat(minMatch[1]));
  // Common phrases: "short nap" ~30 min, "long nap" ~90 min, "quick nap" ~20 min
  if (!durationMin && /quick|power/.test(t)) durationMin = 20;
  if (!durationMin && /short/.test(t))        durationMin = 30;
  if (!durationMin && /long/.test(t))          durationMin = 90;
  return durationMin > 0 ? { type, durationMin } : null;
}

function startVoiceLog() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) { showToast('Voice not supported on this browser.'); return; }

  const statusEl = $('#dt-voice-status');
  const btn      = $('#dt-voice-log-btn');
  if (statusEl) { statusEl.style.display = ''; statusEl.textContent = 'Listening…'; }
  if (btn) btn.classList.add('dt-voice-log-btn--listening');

  const rec = new SR();
  rec.lang = 'en-US';
  rec.interimResults = false;
  rec.maxAlternatives = 1;
  rec.start();

  rec.addEventListener('result', (e) => {
    const text = e.results[0][0].transcript;
    if (statusEl) statusEl.textContent = `"${text}"`;

    const parsed = parseSleepVoice(text);
    if (!parsed) {
      if (statusEl) statusEl.textContent = 'Couldn\'t understand — try "45 minute nap" or "8 hour night sleep".';
      if (btn) btn.classList.remove('dt-voice-log-btn--listening');
      return;
    }

    const endTime   = Date.now();
    const startTime = endTime - parsed.durationMin * 60000;
    logs.push({ id: newId(), type: parsed.type, startTime, endTime, durationMin: parsed.durationMin, source: 'voice' });
    saveLogs();
    closeOverlay('dt-log-sheet');
    if (statusEl) { statusEl.style.display = 'none'; statusEl.textContent = ''; }
    if (btn) btn.classList.remove('dt-voice-log-btn--listening');
    showToast(`${parsed.type === 'night' ? 'Night sleep' : 'Nap'} logged — ${parsed.durationMin} min`);
    renderHome();
    if (screen === 'history') renderHistory();
  });

  rec.addEventListener('error', () => {
    if (statusEl) statusEl.textContent = 'Microphone error — please try again.';
    if (btn) btn.classList.remove('dt-voice-log-btn--listening');
  });

  rec.addEventListener('end', () => {
    if (btn) btn.classList.remove('dt-voice-log-btn--listening');
  });
}

// Voice from home screen (uses #dt-voice-hint for status)
function startVoiceFromHome() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) { showToast('Voice not supported on this browser.'); return; }
  const hintEl = $('#dt-voice-hint');
  const btn    = $('#dt-log-cta-voice');
  if (hintEl) { hintEl.style.display = ''; hintEl.textContent = 'Listening…'; }
  if (btn) btn.classList.add('dt-voice-log-btn--listening');
  const rec = new SR();
  rec.lang = 'en-US'; rec.interimResults = false; rec.maxAlternatives = 1;
  rec.start();
  rec.addEventListener('result', (e) => {
    const text   = e.results[0][0].transcript;
    const parsed = parseSleepVoice(text);
    if (!parsed) {
      if (hintEl) hintEl.textContent = 'Try "45 minute nap" or "8 hour night sleep"';
      if (btn) btn.classList.remove('dt-voice-log-btn--listening');
      return;
    }
    const endTime = Date.now(), startTime = endTime - parsed.durationMin * 60000;
    logs.push({ id: newId(), type: parsed.type, startTime, endTime, durationMin: parsed.durationMin, source: 'voice' });
    saveLogs();
    if (hintEl) { hintEl.textContent = `✓ ${parsed.type === 'night' ? 'Night sleep' : 'Nap'} — ${parsed.durationMin} min`; }
    if (btn) btn.classList.remove('dt-voice-log-btn--listening');
    renderHome();
  });
  rec.addEventListener('error', () => {
    if (hintEl) hintEl.textContent = 'Mic error — try again';
    if (btn) btn.classList.remove('dt-voice-log-btn--listening');
  });
  rec.addEventListener('end', () => { if (btn) btn.classList.remove('dt-voice-log-btn--listening'); });
}

// Voice from inside the manual overlay — fills the time fields
function startManualVoice() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) { showToast('Voice not supported on this browser.'); return; }
  const statusEl = $('#dt-manual-voice-status');
  const btn      = $('#dt-manual-voice-btn');
  if (statusEl) { statusEl.style.display = ''; statusEl.textContent = 'Listening…'; }
  if (btn) btn.classList.add('dt-voice-log-btn--listening');
  const rec = new SR();
  rec.lang = 'en-US'; rec.interimResults = false; rec.maxAlternatives = 1;
  rec.start();
  rec.addEventListener('result', (e) => {
    const text   = e.results[0][0].transcript;
    const parsed = parseSleepVoice(text);
    if (!parsed) {
      if (statusEl) statusEl.textContent = 'Try "45 minute nap" or "8 hour night sleep"';
      if (btn) btn.classList.remove('dt-voice-log-btn--listening');
      return;
    }
    // Fill type
    $$('.dt-huck-type-btn').forEach(b => {
      b.classList.toggle('dt-huck-type-btn--active', b.dataset.stype === parsed.type);
    });
    // Fill times from duration (end=now, start=now−duration)
    const endTime = Date.now(), startTime = endTime - parsed.durationMin * 60000;
    const fmt = ts => { const d = new Date(ts); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; };
    const startEl = $('#dt-manual-start'), endEl = $('#dt-manual-end');
    if (startEl) startEl.value = fmt(startTime);
    if (endEl)   endEl.value   = fmt(endTime);
    updateManualDurationHuck();
    if (statusEl) statusEl.textContent = `✓ ${parsed.durationMin} min ${parsed.type}`;
    if (btn) btn.classList.remove('dt-voice-log-btn--listening');
  });
  rec.addEventListener('error', () => {
    if (statusEl) statusEl.textContent = 'Mic error — try again';
    if (btn) btn.classList.remove('dt-voice-log-btn--listening');
  });
  rec.addEventListener('end', () => { if (btn) btn.classList.remove('dt-voice-log-btn--listening'); });
}

// ── MANUAL ENTRY ──────────────────────────────────────────────────────────────
function openManualOverlay() {
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const endH = pad(now.getHours()), endM = pad(now.getMinutes());
  const startH = pad(now.getHours() > 0 ? now.getHours() - 1 : 23);

  if ($('#dt-manual-date'))  $('#dt-manual-date').value  = todayStr;
  if ($('#dt-manual-start')) $('#dt-manual-start').value = `${startH}:${endM}`;
  if ($('#dt-manual-end'))   $('#dt-manual-end').value   = `${endH}:${endM}`;

  // Set Huckleberry type buttons (default to nap)
  $$('.dt-huck-type-btn').forEach((b, i) => b.classList.toggle('dt-huck-type-btn--active', i === 0));
  updateManualDurationHuck();
  openOverlay('dt-overlay-manual');
}

function updateManualDuration() {
  const preview = $('#dt-manual-duration-preview');
  if (!preview) return;
  const dateVal  = $('#dt-manual-date')?.value;
  const startVal = $('#dt-manual-start')?.value;
  const endVal   = $('#dt-manual-end')?.value;
  if (!dateVal || !startVal || !endVal) { preview.textContent = 'Duration: —'; return; }
  let startMs = new Date(`${dateVal}T${startVal}`).getTime();
  let endMs   = new Date(`${dateVal}T${endVal}`).getTime();
  if (endMs <= startMs) endMs += 86400000;
  const min = Math.round((endMs - startMs) / 60000);
  if (min <= 0) { preview.textContent = 'Duration: —'; return; }
  preview.textContent = `Duration: ${formatDuration(min)}`;
}

function saveManualSleep() {
  const dateVal  = $('#dt-manual-date')?.value;
  const startVal = $('#dt-manual-start')?.value;
  const endVal   = $('#dt-manual-end')?.value;
  // Support both old class and new Huckleberry class
  const type = ($('.dt-huck-type-btn--active') || $('.dt-manual-type-btn--active'))?.dataset.stype || 'nap';
  if (!dateVal || !startVal || !endVal) { showToast('Please fill in all fields.'); return; }
  let startTime = new Date(`${dateVal}T${startVal}`).getTime();
  let endTime   = new Date(`${dateVal}T${endVal}`).getTime();
  if (endTime <= startTime) endTime += 86400000;
  const durationMin = Math.round((endTime - startTime) / 60000);
  if (durationMin <= 0) { showToast('End time must be after start time.'); return; }
  logs.push({ id: newId(), type, startTime, endTime, durationMin, source: 'manual' });
  saveLogs();
  closeOverlay('dt-overlay-manual');
  showToast('Sleep saved!');
  renderHome();
  if (screen === 'history') renderHistory();
}

function updateManualDurationHuck() {
  const preview = $('#dt-manual-duration-preview');
  if (!preview) return;
  const dateVal  = $('#dt-manual-date')?.value;
  const startVal = $('#dt-manual-start')?.value;
  const endVal   = $('#dt-manual-end')?.value;
  if (!dateVal || !startVal || !endVal) { preview.textContent = '—'; return; }
  let startMs = new Date(`${dateVal}T${startVal}`).getTime();
  let endMs   = new Date(`${dateVal}T${endVal}`).getTime();
  if (endMs <= startMs) endMs += 86400000;
  const min = Math.round((endMs - startMs) / 60000);
  preview.textContent = min > 0 ? formatDuration(min) : '—';
}

// ── CSV EXPORT ────────────────────────────────────────────────────────────────
function downloadCSV() {
  const rows = ['type,start,end,duration_min,method,settle_min,quality,night_wakings,notes'];
  logs.forEach(l => {
    rows.push([
      l.type,
      new Date(l.startTime).toISOString(),
      l.endTime ? new Date(l.endTime).toISOString() : '',
      l.durationMin || '',
      l.method || '',
      l.settleMin || '',
      l.quality || '',
      l.nightWakings ?? '',
      (l.notes || '').replace(/,/g, ' ')
    ].join(','));
  });
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = 'dreamlog-sleep-history.csv';
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── EVENT BINDING ─────────────────────────────────────────────────────────────
function bindEvents() {
  // Nav
  $$('.dt-nav-item[data-screen]').forEach(btn => btn.addEventListener('click', () => navigate(btn.dataset.screen)));
  $('#dt-nav-add')?.addEventListener('click', openLogSheet);

  // Topbar settings
  $('#dt-topbar-settings')?.addEventListener('click', openSettings);

  // Child selector
  $('#dt-child-selector-btn')?.addEventListener('click', switchSleepChild);

  // Single Log Sleep CTA + voice
  $('#dt-log-cta-btn')?.addEventListener('click',   () => openManualOverlay());
  $('#dt-log-cta-voice')?.addEventListener('click', startVoiceFromHome);

  // Log sheet
  $('#dt-log-sheet-close')?.addEventListener('click', () => closeOverlay('dt-log-sheet'));
  $('#dt-log-sheet-past')?.addEventListener('click', openManualOverlay);
  $('#dt-log-end-btn')?.addEventListener('click', endSleepFromSheet);

  // Quick-log type buttons (in the no-timer sheet)
  $$('#dt-log-start .dt-log-type-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      $$('#dt-log-start .dt-log-type-btn').forEach(b => b.classList.remove('dt-log-type-btn--active'));
      btn.classList.add('dt-log-type-btn--active');
    });
  });

  // Quick-save button
  $('#dt-quick-save-btn')?.addEventListener('click', saveQuickSleep);

  // Voice log button
  $('#dt-voice-log-btn')?.addEventListener('click', startVoiceLog);

  // Log type toggle (end sheet)
  $$('.dt-log-type-btn').forEach(btn => btn.addEventListener('click', () => {
    $$('.dt-log-type-btn').forEach(b => b.classList.remove('dt-log-type-btn--active'));
    btn.classList.add('dt-log-type-btn--active');
    const nightGroup = $('#dt-log-night-wakings-group');
    if (nightGroup) nightGroup.style.display = btn.dataset.ltype === 'night' ? '' : 'none';
  }));

  // Details toggle
  $('#dt-log-details-toggle')?.addEventListener('click', () => {
    const details = $('#dt-log-details');
    const toggle  = $('#dt-log-details-toggle');
    if (!details) return;
    const open = details.style.display !== 'none';
    details.style.display = open ? 'none' : '';
    toggle?.classList.toggle('dt-log-details-toggle--open', !open);
  });

  // Detail pills (single-select per group)
  ['dt-log-location-pills', 'dt-log-method-pills', 'dt-log-quality-pills'].forEach(groupId => {
    $$(`#${groupId} .dt-log-pill`).forEach(pill => {
      pill.addEventListener('click', () => {
        const wasActive = pill.classList.contains('dt-log-pill--active');
        $$(`#${groupId} .dt-log-pill`).forEach(p => p.classList.remove('dt-log-pill--active'));
        if (!wasActive) pill.classList.add('dt-log-pill--active');
      });
    });
  });

  // Settle stepper
  $('#dt-settle-minus')?.addEventListener('click', () => { settleVal = Math.max(0, settleVal - 1); const e = $('#dt-settle-val'); if (e) e.textContent = settleVal; });
  $('#dt-settle-plus')?.addEventListener('click',  () => { settleVal++; const e = $('#dt-settle-val'); if (e) e.textContent = settleVal; });

  // Night wakings stepper (in sheet)
  $('#dt-wakings-minus')?.addEventListener('click', () => { wakingsVal = Math.max(0, wakingsVal - 1); const e = $('#dt-wakings-val'); if (e) e.textContent = wakingsVal; });
  $('#dt-wakings-plus')?.addEventListener('click',  () => { wakingsVal++; const e = $('#dt-wakings-val'); if (e) e.textContent = wakingsVal; });

  // Manual (Huckleberry) overlay
  $('#dt-manual-close')?.addEventListener('click', () => closeOverlay('dt-overlay-manual'));
  $('#dt-manual-save')?.addEventListener('click', saveManualSleep);
  $('#dt-manual-voice-btn')?.addEventListener('click', startManualVoice);

  // Huckleberry type buttons
  $$('.dt-huck-type-btn').forEach(btn => btn.addEventListener('click', () => {
    $$('.dt-huck-type-btn').forEach(b => b.classList.remove('dt-huck-type-btn--active'));
    btn.classList.add('dt-huck-type-btn--active');
  }));
  ['#dt-manual-date', '#dt-manual-start', '#dt-manual-end'].forEach(sel => {
    $(sel)?.addEventListener('change', updateManualDurationHuck);
    $(sel)?.addEventListener('input',  updateManualDurationHuck);
  });

  // Bedtime routine overlay
  $('#dt-routine-close')?.addEventListener('click', () => { if (routineTimer) { clearInterval(routineTimer); routineTimer = null; } closeOverlay('dt-overlay-routine'); });
  $('#dt-routine-start-btn')?.addEventListener('click', startRoutineTimer);
  $('#dt-routine-end')?.addEventListener('click', endRoutine);
  $$('.dt-routine-step').forEach(btn => btn.addEventListener('click', () => btn.classList.toggle('dt-routine-step--active')));
  $$('#dt-routine-mood-pills .dt-log-pill').forEach(pill => pill.addEventListener('click', () => {
    const wasActive = pill.classList.contains('dt-log-pill--active');
    $$('#dt-routine-mood-pills .dt-log-pill').forEach(p => p.classList.remove('dt-log-pill--active'));
    if (!wasActive) pill.classList.add('dt-log-pill--active');
  }));

  // Night wakings sheet
  $('#dt-wakings-close-bg')?.addEventListener('click', () => closeOverlay('dt-overlay-wakings'));
  $('#dt-wk-minus')?.addEventListener('click', () => { wkVal = Math.max(0, wkVal - 1); const e = $('#dt-wk-val'); if (e) e.textContent = wkVal; });
  $('#dt-wk-plus')?.addEventListener('click',  () => { wkVal++; const e = $('#dt-wk-val'); if (e) e.textContent = wkVal; });
  $('#dt-wakings-save')?.addEventListener('click', saveNightWakings);

  // Settings overlay
  $('#dt-settings-close')?.addEventListener('click', () => closeOverlay('dt-overlay-settings'));
  $('#dt-settings-save')?.addEventListener('click', saveSettings);
  $('#dt-download-csv')?.addEventListener('click', downloadCSV);

  // Schedule view toggle
  $$('.dt-view-btn').forEach(btn => btn.addEventListener('click', () => {
    $$('.dt-view-btn').forEach(b => b.classList.remove('dt-view-btn--active'));
    btn.classList.add('dt-view-btn--active');
    renderSchedule(btn.dataset.view);
  }));

  // History filter chips
  $$('.dt-filter-chip').forEach(chip => chip.addEventListener('click', () => {
    $$('.dt-filter-chip').forEach(c => c.classList.remove('dt-filter-chip--active'));
    chip.classList.add('dt-filter-chip--active');
    renderHistory(chip.dataset.filter || 'all');
  }));

  // Insights period toggle
  $$('.dt-period-btn').forEach(btn => btn.addEventListener('click', () => {
    $$('.dt-period-btn').forEach(b => b.classList.remove('dt-period-btn--active'));
    btn.classList.add('dt-period-btn--active');
    renderInsights(btn.dataset.period);
  }));

  // Onboarding
  $('#dt-ob-save')?.addEventListener('click', saveOnboarding);
  $('#dt-ob-close')?.addEventListener('click', () => closeOverlay('dt-onboarding'));
  $$('.dt-ob-sit').forEach(btn => btn.addEventListener('click', () => {
    $$('.dt-ob-sit').forEach(b => b.classList.remove('dt-ob-sit--active'));
    btn.classList.add('dt-ob-sit--active');
  }));

  // Nap guide
  $('#dt-nap-guide-close-bg')?.addEventListener('click', () => closeOverlay('dt-overlay-nap-guide'));
  $('#dt-nap-guide-done')?.addEventListener('click', () => closeOverlay('dt-overlay-nap-guide'));
}

// ── BOOT ──────────────────────────────────────────────────────────────────────
async function boot() {
  loadLogs();
  loadRoutines();
  loadGoals();
  loadPrefs();

  if (!prefs.onboardingDone) await hydrateFromSharedProfile();

  activeSleep = loadActive();

  updateTopbar();
  bindEvents();

  const stage = getStage();
  applyStageUI(stage);

  if (activeSleep) startTimerTick();

  checkOnboarding();
  navigate('home');

  // Hydrate from cloud, then sync local data up if the metafield was empty.
  loadFromCloud().then(() => {
    updateTopbar();
    navigate(screen);
    syncToCloud();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
